// Tenantra Inc. - Identity Verification Page (Step 2 - Didit)
import { useState, useEffect } from 'react';
import { 
  Shield, CheckCircle, Clock, Loader2, XCircle, RefreshCw, 
  ExternalLink, ArrowRight, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';
import { navigate } from '@/services/navigation';

export default function VerificationPage() {
  const [user, setUser] = useState(null);
  const [landlord, setLandlord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingSession, setCreatingSession] = useState(false);
  const [hasWallet, setHasWallet] = useState(true);
  useEffect(() => { api.get('/landlord/wallet').then(r => setHasWallet(!!r.data.wallet)).catch(() => {}); }, []);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setLandlord(response.data.landlord);
      if (!response.data?.phone_verified) {
        window.location.href = '/landlord/verify-phone';
      }
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    try {
      setCreatingSession(true);
      const response = await api.post('/identity/create-session');
      if (response.data.success && response.data.session_url) {
        window.location.href = response.data.session_url;
      } else {
        toast.error(response.data.error || 'Failed to start verification');
      }
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setCreatingSession(false);
    }
  };

  if (loading) {
    return <div data-testid="identity-verify-loading"><TableSkeleton rows={4} spans={[6, 3, 3]} /></div>;
  }

  if (!user?.phone_verified) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <p className="text-sm text-slate-600 mb-3">Phone verification required first.</p>
          <a href="/landlord/verify-phone">
            <Button variant="outline" size="sm" className="text-sm">
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Verify Phone
            </Button>
          </a>
        </div>
      </div>
    );
  }

  const idStatus = landlord?.id_verification_status || 'incomplete';
  const idVerified = idStatus === 'verified';

  if (idVerified) {
    return (
      <div className="max-w-sm mx-auto pt-6 sm:pt-10" data-testid="identity-verified">
        {/* Progress — both done */}
        <div className="flex items-center gap-1.5 mb-5">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white">
            <CheckCircle className="w-4 h-4" />
          </div>
          <div className="flex-1 h-0.5 rounded-full bg-emerald-500" />
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white">
            <CheckCircle className="w-4 h-4" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 shadow-sm text-center">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-6 h-6 text-emerald-500" />
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-1">Verification Complete</h2>
          <p className="text-sm text-slate-500 mb-4">Your identity has been verified. You're all set.</p>
          {!hasWallet && (
            <Button onClick={() => navigate('/landlord/wallet')} className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0" data-testid="verification-setup-wallet">
              Set up rent wallet <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          )}
          <button onClick={() => navigate('/landlord/security')} className={hasWallet ? 'w-full h-9 rounded-md bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm' : 'block mx-auto mt-2 text-xs text-slate-500 hover:text-teal-700'} data-testid="verification-back-to-security">Back to Security</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto pt-6 sm:pt-10" data-testid="identity-verify-page">
      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-5">
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white">
          <CheckCircle className="w-4 h-4" />
        </div>
        <div className="flex-1 h-0.5 rounded-full bg-teal-500" />
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-teal-600 text-white font-bold text-xs">2</div>
      </div>

      {/* Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-slate-800 mb-1">Verify your identity</h2>
          <p className="text-sm text-slate-500">Scan your government-issued ID to continue</p>
        </div>

        {idStatus === 'pending' ? (
          <div className="space-y-4 text-center">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
              <Clock className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Verification in progress</p>
              <p className="text-xs text-slate-400 mt-1">This usually takes a few minutes.</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUserData}
              className="text-sm"
              data-testid="check-status-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Check Status
            </Button>
          </div>
        ) : idStatus === 'rejected' || idStatus === 'failed' ? (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
              <p className="text-sm font-medium text-slate-700">
                Verification {idStatus === 'rejected' ? 'rejected' : 'failed'}
              </p>
              <p className="text-xs text-slate-400 mt-1">Please try again with a clear photo of your valid ID.</p>
            </div>
            <Button
              onClick={startSession}
              disabled={creatingSession}
              className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0"
              data-testid="retry-verify-btn"
            >
              {creatingSession ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Preparing...
                </span>
              ) : (
                'Try Again'
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-teal-600" />
              </div>
              <p className="text-sm text-slate-500">
                You'll be redirected to scan your ID<br />
                <span className="text-xs text-slate-400">(passport, driver's licence, or national ID)</span>
              </p>
            </div>

            <div className="space-y-1.5 text-xs text-slate-500">
              <p className="font-medium text-slate-600 text-sm">You'll need:</p>
              {['Valid government-issued photo ID', 'Good lighting for clear photos', 'A few minutes to complete'].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={startSession}
              disabled={creatingSession}
              className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0"
              data-testid="start-verify-btn"
            >
              {creatingSession ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Preparing...
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  Start Verification <ExternalLink className="w-3.5 h-3.5" />
                </span>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Compact footer */}
      <p className="text-xs text-slate-400 text-center mt-4">
        Identity verification protects tenant data and meets Canadian privacy standards.
      </p>
    </div>
  );
}
